import { diffChars, diffWordsWithSpace } from "diff";
import { sha256 } from "js-sha256";

export type PromptDiffToken = {
  type: "equal" | "insert" | "delete";
  value: string;
};
export type LogContext = {
  description: string;
  source?: "chat" | "manual";
  request?: string;
  module?: string | null;
  createdAt?: number;
  undoOf?: number;
  banWords?: { before: string[]; after: string[] };
};
/** Legacy/display shape only. Never persist this shape in new histories. */
export type PromptLog = LogContext & { diff: PromptDiffToken[] };
export type PromptPatch = {
  start: number;
  deleteCount: number;
  insertText: string;
};
export type PatchLog = LogContext & {
  sequence: number;
  beforeHash: string;
  afterHash: string;
  patches: PromptPatch[];
};
export type ResolvedPromptLog = PromptLog & {
  beforePrompt: string;
  afterPrompt: string;
};

export function promptWordDiff(
  before: string,
  after: string
): PromptDiffToken[] {
  return diffWordsWithSpace(before, after).map((change) => ({
    type: change.added ? "insert" : change.removed ? "delete" : "equal",
    value: change.value,
  }));
}

/** SHA-256 over UTF-16LE code units: exact JS strings, including lone surrogates. */
export function promptHash(text: string): string {
  const bytes = new Uint8Array(text.length * 2);
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i);
    bytes[i * 2] = unit & 255;
    bytes[i * 2 + 1] = unit >>> 8;
  }
  return sha256(bytes);
}

export function applyPatches(before: string, patches: PromptPatch[]): string {
  if (!Array.isArray(patches)) throw new Error("修改补丁格式无效");
  let end = 0,
    previousStart = -1;
  for (const patch of patches) {
    if (
      !patch ||
      !Number.isSafeInteger(patch.start) ||
      !Number.isSafeInteger(patch.deleteCount) ||
      patch.start < 0 ||
      patch.deleteCount < 0 ||
      typeof patch.insertText !== "string" ||
      patch.start + patch.deleteCount > before.length ||
      patch.start < end ||
      patch.start <= previousStart ||
      (!patch.deleteCount && !patch.insertText)
    )
      throw new Error("修改补丁越界、乱序、重叠或为空");
    end = patch.start + patch.deleteCount;
    previousStart = patch.start;
  }
  let result = before;
  for (let i = patches.length - 1; i >= 0; i--) {
    const patch = patches[i];
    result =
      result.slice(0, patch.start) +
      patch.insertText +
      result.slice(patch.start + patch.deleteCount);
  }
  return result;
}

export function createPatches(before: string, after: string): PromptPatch[] {
  let offset = 0;
  const patches: PromptPatch[] = [];
  let active: PromptPatch | undefined;
  for (const change of diffChars(before, after)) {
    if (!change.added && !change.removed) {
      offset += change.value.length;
      active = undefined;
      continue;
    }
    if (!active) {
      active = { start: offset, deleteCount: 0, insertText: "" };
      patches.push(active);
    }
    if (change.removed) {
      active.deleteCount += change.value.length;
      offset += change.value.length;
    } else active.insertText += change.value;
  }
  if (applyPatches(before, patches) !== after)
    throw new Error("生成的补丁无法还原修改结果");
  return patches;
}

export function createPatchLog(
  before: string,
  after: string,
  sequence: number,
  context: LogContext
): PatchLog {
  if (
    before === after &&
    (!context.banWords ||
      sameBanWords(context.banWords.before, context.banWords.after))
  )
    throw new Error("没有实际修改，不应新增日志");
  return {
    ...context,
    sequence,
    beforeHash: promptHash(before),
    afterHash: promptHash(after),
    patches: createPatches(before, after),
  };
}

export function sameBanWords(a: string[], b: string[]) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function validateBanHistory(value: unknown): asserts value is string[] {
  if (
    !Array.isArray(value) ||
    value.length > 300 ||
    value.some(
      (word) =>
        typeof word !== "string" ||
        !word ||
        word.length > 46 ||
        word !== word.trim().replace(/\s+/g, " ").toLowerCase()
    ) ||
    new Set(value).size !== value.length
  )
    throw new Error("禁止标签历史格式无效");
}

export function replayHistory(
  basePrompt: string,
  logs: PatchLog[],
  finalPrompt?: string,
  bans?: { base: string[]; final?: string[] }
): {
  log: PatchLog;
  before: string;
  after: string;
  beforeBanWords: string[];
  afterBanWords: string[];
}[] {
  let text = basePrompt;
  let words =
    bans?.base ?? logs.find((log) => log.banWords)?.banWords?.before ?? [];
  validateBanHistory(words);
  const active: { sequence: number; before: string; words: string[] }[] = [];
  const entries = logs.map((log, index) => {
    if (
      !log ||
      log.sequence !== index + 1 ||
      log.beforeHash !== promptHash(text)
    )
      throw new Error(`第 ${index + 1} 条修改记录顺序或前文哈希不一致`);
    const before = text;
    const beforeBanWords = words;
    if (log.banWords) {
      validateBanHistory(log.banWords.before);
      validateBanHistory(log.banWords.after);
      if (!sameBanWords(words, log.banWords.before))
        throw new Error("禁止标签修改记录不连续");
      words = log.banWords.after;
    }
    text = applyPatches(before, log.patches);
    if (
      (text === before && sameBanWords(words, beforeBanWords)) ||
      log.afterHash !== promptHash(text)
    )
      throw new Error(`第 ${index + 1} 条修改记录后文哈希不一致或没有改动`);
    if (text.length > 16000) throw new Error("修改记录中的提示词超过 16000 字");
    if (log.undoOf !== undefined) {
      const target = active.pop();
      if (
        !target ||
        target.sequence !== log.undoOf ||
        text !== target.before ||
        !sameBanWords(words, target.words)
      )
        throw new Error("撤销记录与上一次有效修改不一致");
    } else
      active.push({ sequence: log.sequence, before, words: beforeBanWords });
    return { log, before, after: text, beforeBanWords, afterBanWords: words };
  });
  if (finalPrompt !== undefined && text !== finalPrompt)
    throw new Error("修改记录与最终提示词不一致");
  if (bans?.final !== undefined) {
    validateBanHistory(bans.final);
    if (!sameBanWords(words, bans.final))
      throw new Error("修改记录与最终禁止标签不一致");
  }
  return entries;
}

function legacyTexts(log: PromptLog) {
  if (
    !log ||
    typeof log.description !== "string" ||
    !Array.isArray(log.diff) ||
    log.diff.some(
      (token) =>
        !token ||
        !["equal", "insert", "delete"].includes(token.type) ||
        typeof token.value !== "string"
    )
  )
    throw new Error("旧修改记录格式无效");
  return {
    before: log.diff
      .filter((token) => token.type !== "insert")
      .map((token) => token.value)
      .join(""),
    after: log.diff
      .filter((token) => token.type !== "delete")
      .map((token) => token.value)
      .join(""),
  };
}

export function migrateLegacyLogs(
  basePrompt: string,
  logs: PromptLog[],
  finalPrompt: string
): PatchLog[] {
  let text = basePrompt;
  const migrated: PatchLog[] = [];
  for (const log of logs) {
    const { before, after } = legacyTexts(log);
    if (before !== text) throw new Error("旧修改记录不连续，原草稿已保留");
    if (before === after)
      throw new Error("旧修改记录包含无变化记录，原草稿已保留");
    const { diff, ...context } = log;
    migrated.push(createPatchLog(before, after, migrated.length + 1, context));
    text = after;
  }
  replayHistory(basePrompt, migrated, finalPrompt);
  return migrated;
}

export function resolvePromptLogs(
  logs: (PatchLog | PromptLog)[],
  basePrompt?: string,
  finalPrompt?: string
): ResolvedPromptLog[] {
  if (logs.some((log) => "patches" in log)) {
    if (basePrompt === undefined || logs.some((log) => !("patches" in log)))
      throw new Error("修改记录缺少起点或混合了不兼容的格式");
    return replayHistory(basePrompt, logs as PatchLog[], finalPrompt).map(
      ({ log, before, after }) => ({
        ...log,
        beforePrompt: before,
        afterPrompt: after,
        diff: promptWordDiff(before, after),
      })
    );
  }
  if (
    !logs.length &&
    basePrompt !== undefined &&
    finalPrompt !== undefined &&
    basePrompt !== finalPrompt
  )
    throw new Error("修改记录缺失，无法还原最终提示词");
  return (logs as PromptLog[]).map((log) => {
    const { before, after } = legacyTexts(log);
    return { ...log, beforePrompt: before, afterPrompt: after };
  });
}
